import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

function saveBlob(data, filename, type) {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function DataPorter({ entity, onImported, showImport = true }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/template/${entity}`, { responseType: "blob" });
      saveBlob(r.data, `${entity}_import_template.xlsx`, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      toast.success("Excel template downloaded");
    } catch (e) { toast.error("Template download failed"); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/export/${entity}`, { responseType: "blob" });
      saveBlob(r.data, `${entity}_export.csv`, "text/csv");
      toast.success("Exported to CSV");
    } catch (e) { toast.error("Export failed"); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = (ev) => setCsv(ev.target.result);
    rd.readAsText(f);
  };

  const doImport = async () => {
    if (!csv.trim()) return toast.error("Paste or upload CSV first");
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${entity}`, { csv });
      toast.success(`Imported ${data.created} records`);
      setOpen(false); setCsv("");
      onImported && onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" data-testid={`template-${entity}-button`} onClick={downloadTemplate}>
        <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel Template
      </Button>
      <Button variant="outline" data-testid={`export-${entity}-button`} onClick={exportCsv}>
        <Download className="h-4 w-4 mr-1.5" /> Export CSV
      </Button>
      {showImport && (<>
      <Button variant="outline" data-testid={`import-${entity}-button`} onClick={() => setOpen(true)}>
        <Upload className="h-4 w-4 mr-1.5" /> Import CSV
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Import {entity}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            Download the <b>Excel Template</b> for the exact columns. Only <b>name</b> is required; list fields use <code className="text-ccc-blue">|</code> as separator. Blank cells fall back to defaults.
          </p>
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid={`import-file-${entity}`} className="text-sm" />
          <Textarea data-testid={`import-csv-${entity}`} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="Paste CSV rows here…" className="font-mono text-xs h-40" />
          <DialogFooter>
            <Button data-testid={`import-submit-${entity}`} disabled={busy} onClick={doImport} className="bg-ccc-emerald hover:bg-emerald-600 text-white">
              {busy ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
    </>
  );
}
